import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { saveSaleRecord } from "@/lib/pipelineApi";
import { errMsg } from "@/lib/applyApi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { WithdrawFields } from "./WithdrawFields";

export const OUTCOMES = [["sold", "Sold"], ["unsold", "Not Sold"], ["withdrawn", "Withdrawn"]];
const num = (v) => (v === "" || v == null ? null : Number(String(v).replace(/[^0-9.]/g, "")));
export const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);

const initial = (r, mode) => ({
  outcome: mode === "withdraw" ? "withdrawn" : r.outcome || "", final_bid: r.final_bid ?? "", sale_price: r.sale_price ?? "", sold_at: r.sold_at || "",
  notes: r.notes || "", buyer: { name: "", phone: "", email: "", location: "", ...(r.buyer || {}) },
  commission_percent: r.commission_percent ?? "", commission_amount: r.commission_amount ?? "", manual: r.commission_amount_source === "admin",
  withdrawal_reason: r.withdrawal?.reason || "", withdrawal_explanation: r.withdrawal?.explanation || "", hide: !r.withdrawal || !!r.withdrawal.hidden_from_website,
});

export function SaleRecordForm({ c, mode, onSaved }) {
  const r = c.sale_record || {};
  const [v, setV] = useState(() => initial(r, mode));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const setB = (k) => (e) => setV({ ...v, buyer: { ...v.buyer, [k]: e.target.value } });
  const pct = c.contract_fee_percent ?? num(v.commission_percent);
  const price = num(v.sale_price), bid = num(v.final_bid);
  const calc = v.outcome === "sold" && price != null && pct != null ? Math.round(price * pct) / 100 : null;
  const imported = r.commission_amount_source === "smartsheet" ? r.commission_amount : null;
  const differs = imported != null && calc != null && Math.abs(imported - calc) > 0.005;
  const save = async () => {
    setBusy(true);
    try {
      await saveSaleRecord(c.ids.horse_id, {
        outcome: v.outcome, final_bid: bid, sale_price: price, sold_at: v.sold_at || null, notes: v.notes, buyer: v.buyer,
        commission_percent: c.contract_fee_percent == null ? num(v.commission_percent) : null,
        commission_amount_manual: v.manual, commission_amount: v.manual ? num(v.commission_amount) : null, use_calculated_commission: !!v.useCalc,
        withdrawal_reason: v.withdrawal_reason || null, withdrawal_explanation: v.withdrawal_explanation || null, hide_from_website: v.outcome === "withdrawn" && v.hide,
      });
      toast.success(v.outcome === "withdrawn" ? "Auction withdrawn" : "Sale outcome saved"); onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save outcome")); } finally { setBusy(false); setConfirm(false); }
  };
  const go = () => (v.outcome === "withdrawn" && r.outcome !== "withdrawn" ? setConfirm(true) : save());
  const f = (k, label, val, on, props = {}) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label><Input data-testid={`sale-${k}`} value={val} onChange={on} className="h-11 text-base sm:text-sm" {...props} /></div>
  );
  return (
    <div data-testid="sale-record-form" className="space-y-3 rounded-xl border border-stone-200 p-3">
      {mode !== "withdraw" && (
        <div role="radiogroup" className="flex flex-wrap gap-2">
          {OUTCOMES.map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={v.outcome === k} data-testid={`sale-outcome-${k}`} onClick={() => setV({ ...v, outcome: k })}
              className={`h-11 px-4 rounded-full border text-sm font-semibold ${v.outcome === k ? "bg-stone-700 border-stone-700 text-white" : "bg-white border-stone-300 text-stone-700"}`}>{l}</button>
          ))}
        </div>
      )}
      {v.outcome === "withdrawn" ? <WithdrawFields v={v} setV={setV} /> : <>
        <div className="grid grid-cols-2 gap-3">
          {f("final-bid", "Final auction bid", v.final_bid, set("final_bid"), { inputMode: "decimal" })}
          {f("sale-price", "Actual sale price", v.sale_price, set("sale_price"), { inputMode: "decimal" })}
        </div>
        <p data-testid="sale-difference" className="text-xs text-stone-600">Difference (sale price − final bid): <b>{price != null && bid != null ? money(price - bid) : "—"}</b></p>
        <div className="grid grid-cols-2 gap-3">
          {c.contract_fee_percent != null
            ? <div className="space-y-1"><Label className="text-xs">Commission %</Label><p data-testid="sale-commission-percent-contract" className="h-11 flex items-center text-sm font-semibold">{c.contract_fee_percent}% <span className="ml-1 font-normal text-stone-500">(signed contract)</span></p></div>
            : f("commission-percent", "Commission % (e.g. 5 or 10)", v.commission_percent, set("commission_percent"), { inputMode: "decimal", placeholder: "Unknown" })}
          {v.manual ? f("commission-amount", "Commission amount", v.commission_amount, set("commission_amount"), { inputMode: "decimal" })
            : <div className="space-y-1"><Label className="text-xs">Commission amount</Label><p data-testid="sale-commission-calc" className="h-11 flex items-center text-sm font-semibold">{imported != null && !v.useCalc ? `${money(imported)} (Smartsheet)` : calc != null ? money(calc) : v.outcome === "sold" ? "Needs sale price and %" : "Only for Sold"}</p></div>}
        </div>
        {differs && !v.manual && (
          <div data-testid="sale-commission-differs" className="text-xs rounded-md border border-amber-200 bg-amber-50 p-2 space-y-1">
            <p>Imported from Smartsheet: <b>{money(imported)}</b> · Calculated from the sale price: <b>{money(calc)}</b></p>
            <label className="flex items-center gap-2"><Checkbox data-testid="sale-commission-use-calc" checked={!!v.useCalc} onCheckedChange={(x) => setV({ ...v, useCalc: !!x })} />Replace the Smartsheet amount with the calculated amount</label>
          </div>
        )}
        <label className="flex items-center gap-2 text-xs text-stone-700"><Checkbox data-testid="sale-commission-manual" checked={v.manual} onCheckedChange={(x) => setV({ ...v, manual: !!x, commission_amount: v.commission_amount === "" && calc != null ? calc : v.commission_amount })} />Enter the commission amount myself</label>
        {f("sold-at", "Sale date", v.sold_at, set("sold_at"), { type: "date" })}
        <div className="grid grid-cols-2 gap-3">
          {f("buyer-name", "Buyer name", v.buyer.name, setB("name"))}
          {f("buyer-phone", "Buyer phone", v.buyer.phone, setB("phone"), { type: "tel", inputMode: "tel" })}
          {f("buyer-email", "Buyer email", v.buyer.email, setB("email"), { type: "email" })}
          {f("buyer-location", "Buyer location", v.buyer.location, setB("location"))}
        </div>
      </>}
      <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea data-testid="sale-notes" rows={3} value={v.notes} onChange={set("notes")} className="text-base sm:text-sm" /></div>
      <p className="text-[11px] text-stone-500">Private to admins. Facebook posts and the ad text are never changed.</p>
      <Button data-testid="sale-save" onClick={go} disabled={busy || !v.outcome} className="w-full h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : v.outcome === "withdrawn" && r.outcome !== "withdrawn" ? "Withdraw Auction" : "Save Outcome"}
      </Button>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="withdraw-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw {c.name}?</AlertDialogTitle>
            <AlertDialogDescription>It stops being an active auction{v.hide ? " and is hidden from the website" : ""}. Horse details, bids and Facebook history are kept. Facebook Group and Page posts are not changed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="withdraw-cancel">Keep auction</AlertDialogCancel>
            <AlertDialogAction data-testid="withdraw-confirm-btn" onClick={(e) => { e.preventDefault(); save(); }} disabled={busy}>Withdraw</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
